import React from 'react'
import classes from '@src/styles/disposal/hexagon-features.module.css'
import SolarPower from '@src/assets/solar-panel-disposal/solar_power.svg'
import Roofing from '@src/assets/solar-panel-disposal/roofing.svg'
import Flash from '@src/assets/solar-panel-disposal/flash.svg'
import WindPower from '@src/assets/solar-panel-disposal/wind_power.svg'
import Diversity from '@src/assets/solar-panel-disposal/diversity.svg'

export type HexFeature = {
  heading: string
  /** Icon image path/URL. Omit to render the label alone. */
  src?: string
  /** Optional alt text. Defaults to "" (decorative — the label carries the meaning). */
  alt?: string
}

export type HexagonFeaturesProps = {
  list?: HexFeature[]
  heading?: string
  className?: string
}

/**
 * Canonical solar audiences, from the app repo's `solar-panel-disposal-data.ts`
 * `hexFeatures.list`. The same five render on all three solar pages: the source
 * destructures `hexFeatures` from the disposal content file regardless of route, so
 * the identical blocks in the sell and commercial content files are dead code.
 */
export const DEFAULT_HEX_FEATURES: HexFeature[] = [
  { heading: 'Solar Installers', src: SolarPower.src },
  { heading: 'Commercial roofers', src: Roofing.src },
  { heading: 'Electrical contractors', src: Flash.src },
  { heading: 'Energy and utility companies', src: WindPower.src },
  { heading: 'General contractors', src: Diversity.src },
]

/**
 * "We work with" audience honeycomb for the solar pages — the Next.js port of the app
 * repo's `HexagonFeatures`. The 3-over-2 layout comes entirely from the CSS module's
 * shape-outside float; the markup is a flat list.
 *
 * ⚠️ The CSS module sets fixed `.container` heights per breakpoint (930/720/500px) that
 * assume exactly five items. Passing a different-length `list` will clip the honeycomb
 * or leave a gap — re-do the layout as a grid first if the list ever becomes dynamic.
 */
function HexagonFeatures({
  list = DEFAULT_HEX_FEATURES,
  heading = 'We work with',
  className,
}: HexagonFeaturesProps) {
  return (
    <section className={[classes.mainContainer, className].filter(Boolean).join(' ')}>
      <div className={classes.main}>
        <h2 className={classes.sectionHeading}>{heading}</h2>
        <div className={classes.container}>
          {list.map(({ heading: label, src, alt }) => (
            <div className={classes.hexagon} key={label}>
              <div className={classes.hexagonContent}>
                {src && <img src={src} alt={alt ?? ''} />}
                {label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

export default HexagonFeatures
